/* eslint-disable complexity */
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import Typography from 'carbon-react/lib/components/typography';

import { FormattedMessage } from '@futrli/localisation';
import {
  PreviewAssumptionImpactsResponse,
  PreviewAssumptionImpactsAccountLine,
  PreviewAssumptionImpactsReferenceLine
} from '@futrli-sdk/predict';
import { PreviewAssumptionImpactsResponse as PreviewAssumptionImpactsResponseConsolidation } from '@futrli-sdk/consolidations';

import * as styles from './accounts-impacted.scss';
import { AccountsImpactedGrid } from './accounts-impacted-grid';
// import { Notes } from './notes';
import { FormulaReferenceOption } from '../../../../utils/formulas/types';
import { PredictionType } from '../../types';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

interface Props {
  predictionType: PredictionType | null;
  previewData?: PreviewAssumptionImpactsResponse | PreviewAssumptionImpactsResponseConsolidation;
  error?: boolean;
  advisorFormula?: string;
  formulaEditorOptions?: FormulaReferenceOption[];
}

enum ActiveTab {
  accountsImpacted = 'accounts-impacted',
  notes = 'notes',
  formulaValuesUsed = 'formula-values-used',
  advisorFormula = 'advisor-formula'
}

export const AccountsImpactedContainer = ({
  predictionType,
  previewData,
  error = false,
  advisorFormula = '',
  formulaEditorOptions
}: Props) => {
  const [activeTab, setActiveTab] = useState(ActiveTab.accountsImpacted);
  const [previewDataColumns, setPreviewDataColumns] = useState<string[]>([]);
  const [previewDataAccountLinesImpacted, setPreviewDataAccountLinesImpacted] = useState<
    PreviewAssumptionImpactsAccountLine[]
  >([]);
  const [previewDataLinesReferencedByFormula, setPreviewDataLinesReferencedByFormula] = useState<
    PreviewAssumptionImpactsReferenceLine[] | null
  >([]);

  const handleTabChange = useCallback((tab: ActiveTab) => setActiveTab(tab), []);

  const { forecast } = usePredictionsInfoContext();

  useEffect(() => {
    if (
      !previewData ||
      !previewData.columns ||
      (previewData.columns && !previewData.columns.length) ||
      !previewData.account_lines_impacted.length
    ) {
      return;
    }
    if (forecast?.kind === 'budget') {
      const forecastFromDate = forecast.forecast_from_date;
      // eslint-disable-next-line no-magic-numbers
      const yearMonth = forecastFromDate?.slice(0, 7);
      const forecastFromDateIndex = previewData.columns.indexOf(yearMonth || '');
      setPreviewDataColumns([...previewData.columns.slice(forecastFromDateIndex)]);

      const newLinesImpacted: PreviewAssumptionImpactsAccountLine[] = [];

      for (const lineImpacted of previewData.account_lines_impacted) {
        newLinesImpacted.push({
          ...lineImpacted,
          data: [...lineImpacted.data.slice(forecastFromDateIndex)]
        });
      }
      setPreviewDataAccountLinesImpacted([...newLinesImpacted]);

      if (previewData.lines_referenced_by_formula) {
        const newLinesReferencedByFormula: PreviewAssumptionImpactsReferenceLine[] = [];
        for (const lineReferencedByFormula of previewData.lines_referenced_by_formula) {
          newLinesReferencedByFormula.push({
            ...lineReferencedByFormula,
            name:
              formulaEditorOptions?.find(o => o.id === lineReferencedByFormula.reference)?.name ||
              lineReferencedByFormula.name,
            data: [...lineReferencedByFormula.data.slice(forecastFromDateIndex)]
          });
        }
        setPreviewDataLinesReferencedByFormula([...newLinesReferencedByFormula]);
      }
    } else {
      setPreviewDataColumns([...previewData.columns]);
      setPreviewDataAccountLinesImpacted([...previewData.account_lines_impacted]);
      setPreviewDataLinesReferencedByFormula(
        previewData.lines_referenced_by_formula
          ? [
              ...previewData.lines_referenced_by_formula.map(d => ({
                ...d,
                name: formulaEditorOptions?.find(o => o.id === d.reference)?.name || d.name
              }))
            ]
          : null
      );
    }
  }, [forecast, formulaEditorOptions, previewData]);

  if (
    !previewData ||
    !previewData.columns ||
    (previewData.columns && !previewData.columns.length) ||
    !previewData.account_lines_impacted.length
  ) {
    return null;
  }

  return (
    <div className={styles.accountsImpactedContainer}>
      <div className={styles.tabs}>
        <div
          className={classNames(styles.tab, {
            [styles.activeTab]: activeTab === ActiveTab.accountsImpacted && !error,
            [styles.activeTabError]: activeTab === ActiveTab.accountsImpacted && error
          })}
          onClick={() => handleTabChange(ActiveTab.accountsImpacted)}
        >
          <FormattedMessage id="predict.accounts_impacted" defaultMessage="Accounts impacted" />
        </div>
        {predictionType === PredictionType.FREESTYLE && advisorFormula && (
          <div
            className={classNames(styles.tab, {
              [styles.activeTab]: activeTab === ActiveTab.advisorFormula && !error,
              [styles.activeTabError]: activeTab === ActiveTab.advisorFormula && error
            })}
            onClick={() => handleTabChange(ActiveTab.advisorFormula)}
          >
            <FormattedMessage id="predict.advisor_formula" defaultMessage="Advisor formula" />
          </div>
        )}
        {predictionType === PredictionType.FORMULA && (
          <div
            className={classNames(styles.tab, {
              [styles.activeTab]: activeTab === ActiveTab.formulaValuesUsed && !error,
              [styles.activeTabError]: activeTab === ActiveTab.formulaValuesUsed && error
            })}
            onClick={() => handleTabChange(ActiveTab.formulaValuesUsed)}
          >
            <FormattedMessage id="predict.formula_values_used" defaultMessage="Formula values used" />
          </div>
        )}
        {/* <div
          className={classNames(styles.tab, {
            [styles.activeTab]: activeTab === ActiveTab.notes
          })}
          onClick={() => handleTabChange(ActiveTab.notes)}
        >
          <FormattedMessage id="common.notes" defaultMessage="Notes" />
        </div> */}
      </div>
      {activeTab === ActiveTab.accountsImpacted && (
        <AccountsImpactedGrid columns={previewDataColumns} impactedLines={previewDataAccountLinesImpacted || []} />
      )}
      {/* {activeTab === ActiveTab.notes && <Notes />} */}
      {activeTab === ActiveTab.formulaValuesUsed && predictionType === PredictionType.FORMULA && (
        <AccountsImpactedGrid
          columns={previewDataColumns}
          impactedLines={
            ((previewDataLinesReferencedByFormula || []) as unknown) as PreviewAssumptionImpactsAccountLine[]
          }
        />
      )}
      {activeTab === ActiveTab.advisorFormula && predictionType === PredictionType.FREESTYLE && (
        <div className={styles.advisorFormulaContainer}>
          <Typography mb={0} fontSize="16px">
            {advisorFormula}
          </Typography>
        </div>
      )}
    </div>
  );
};
